import omitProps from '@hocs/omit-props';
import { always, filter, join, keys, map, pickBy, pipe } from 'ramda';
import { compose, setDisplayName, withProps, withStateHandlers } from 'recompose';
import { MediaType } from '../../../constants/MediaType';
import { areAllMediaTypesDisabled, areAllMediaTypesEnabled, areNotAllMediaTypesDisabled } from '../../mediaType';
import { withMMGRSettings, withoutMMGRSettings } from '../withMMGRSettings';
import { IMMGRSettingsProps } from '../withMMGRSettings/withMMGRSettings.types';
import {
  ALL_NON_SECURE_MEDIA_TYPES_SELECTED,
  EVERY_NON_SECURE_MEDIA_TYPE_DISABLED,
  NONE_NON_SECURE_MEDIA_TYPE_SELECTED,
} from './constants';
import { getAllSelectedMediaTypes, getNoneSelectedMediaTypes, parseInitiallySelectedMediaTypes } from './utils';

import {
  IAllMediaTypesSelectedProps,
  IState,
  IWithMediaTypesInner,
  IWithMediaTypesOuter,
  THandlers,
  TMediaTypes,
} from './withMediaTypes.types';

const buildMediaTypes = (mode: boolean = true) => pipe(map(always(mode)))(EVERY_NON_SECURE_MEDIA_TYPE_DISABLED);

export const withMediaTypes = compose<IWithMediaTypesInner, IWithMediaTypesOuter>(
  setDisplayName('withMediaTypes'),
  withMMGRSettings,
  withProps(({ mmgrSettings: { namespace } }) => {
    const allMediaTypesSelected = getAllSelectedMediaTypes(namespace);
    const noneMediaTypesSelected = getNoneSelectedMediaTypes(namespace);

    return {
      allMediaTypesSelected:
        allMediaTypesSelected === ALL_NON_SECURE_MEDIA_TYPES_SELECTED ? buildMediaTypes(true) : allMediaTypesSelected,
      noneMediaTypesSelected:
        noneMediaTypesSelected === NONE_NON_SECURE_MEDIA_TYPE_SELECTED
          ? buildMediaTypes(false)
          : noneMediaTypesSelected,
    };
  }),
  withStateHandlers<IState, THandlers, IWithMediaTypesOuter & IMMGRSettingsProps & IAllMediaTypesSelectedProps>(
    ({ noneMediaTypesSelected, allMediaTypesSelected, mmgrSettings: { namespace, initialMediaTypes } }) => {
      const parsedMediaTypes = parseInitiallySelectedMediaTypes(allMediaTypesSelected, namespace, initialMediaTypes);
      const mediaTypes = areAllMediaTypesEnabled(parsedMediaTypes) ? noneMediaTypesSelected : parsedMediaTypes;

      return {
        previousMediaTypes: mediaTypes,
        mediaTypes,
      };
    },
    {
      selectAllMediaTypes:
        ({ mediaTypes }, { noneMediaTypesSelected }) =>
        () => ({
          previousMediaTypes: mediaTypes,
          mediaTypes: noneMediaTypesSelected,
        }),
      setMediaType:
        ({ mediaTypes }) =>
        (mediaType, checked) => ({
          previousMediaTypes: mediaTypes,
          mediaTypes: { ...mediaTypes, [mediaType]: checked },
        }),
      restorePreviousMediaTypes:
        ({ previousMediaTypes }) =>
        () => ({ mediaTypes: previousMediaTypes }),
    },
  ),
  withoutMMGRSettings,
  withProps(({ allMediaTypesSelected, mediaTypes }) => {
    const allMediaTypesDisabled = areAllMediaTypesDisabled(mediaTypes);
    const mediaTypesSelected = allMediaTypesDisabled ? allMediaTypesSelected : mediaTypes;
    const areNotAllMediaTypesSelected: boolean = areNotAllMediaTypesDisabled(mediaTypes);
    const mediaType = pipe<TMediaTypes, TMediaTypes, string[], string>(
      filter(Boolean),
      keys,
      join(','),
    )(mediaTypesSelected);

    return {
      areNotAllMediaTypesSelected,
      mediaType,
    };
  }),
  omitProps('previousMediaTypes'),
);
